import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';
import { zodDeliveryNoteChainResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type DeliveryNoteChainsResponse } from './types';
import { zodDeliveryNoteChainsAdminPayloadSchema } from './validations';

/**
 * Fetches delivery note chains via GET request.
 * @param deliveryNoteAssetIds - Delivery note asset UUIDs to fetch. NOT chain ids like the customer endpoint!
 * @param options - Additional options for the fetch request.
 * @returns An array of delivery note chains data.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_chain_elements_for_delivery_notes_admin_asset_delivery_note_ids_chains_admin_get
 */
export const fetchDeliveryNoteChainsAdmin = async (
  deliveryNoteAssetIds: UUID[],
  options: KyRequestOptions,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.DELIVERY.GET_CHAINS_ADMIN(), {
        searchParams: { ids: deliveryNoteAssetIds },
        validate: { searchParams: zodDeliveryNoteChainsAdminPayloadSchema },
        ...options,
      })
      .json<DeliveryNoteChainsResponse>();

    // Validate the returned data and log any validation errors
    for (const asset of response.assets) {
      const validatedResponse =
        zodDeliveryNoteChainResponseSchema.safeParse(asset);

      if (validatedResponse?.error?.issues?.length) {
        log.error({
          message: 'Delivery note chains API response has validation errors',
          error: {
            errors: validatedResponse?.error?.issues,
            errorMessage: validatedResponse?.error?.message,
            response: asset,
          },
        });
      }
    }

    return response.assets ?? [];
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Note Chains Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.deliveryNote.getChainsAdmin.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getChainsAdmin.error.generic,
            {
              ns: 'data',
            },
          );
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
